import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { TYPE_LABELS } from "@/types/movie";

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  const next = useCallback(() => {
    setIndex((i) => (i + 1) % items.length);
  }, [items.length]);

  const prev = () => setIndex((i) => (i - 1 + items.length) % items.length);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, items.length]);

  if (items.length === 0) return null;
  const active = items[index];

  return (
    <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden bg-background-100">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} 剧照`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-background-50/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-background-50/90 via-background-50/40 to-transparent" />

      {/* Content */}
      <div className="relative z-10 w-full h-full px-4 md:px-8 lg:px-12 flex items-center">
        <div className="max-w-2xl" key={active.id}>
          <div className="flex items-center gap-3 mb-4 animate-fade-up">
            <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-heading font-bold">
              热门推荐
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-200/80 backdrop-blur text-foreground-800 text-xs">
              {TYPE_LABELS[active.type]}
            </span>
            <span className="flex items-center gap-1 text-accent-500 text-sm font-heading font-bold">
              <i className="ri-star-fill" />
              {active.rating.toFixed(1)}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 leading-tight animate-fade-up">
            {active.title}
          </h1>
          <p className="mt-3 text-xs md:text-sm text-foreground-600 animate-fade-up">
            {active.year} · {active.region} · {active.genres.join(" / ")} · {active.duration}
          </p>
          <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed line-clamp-2 md:line-clamp-3 max-w-xl animate-fade-up">
            {active.summary}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3 animate-fade-up">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </Link>
            <Link
              to={`/category/${active.type}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-200/70 backdrop-blur text-foreground-900 text-sm font-semibold hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
            >
              更多{TYPE_LABELS[active.type]}
            </Link>
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button
        type="button"
        aria-label="上一张"
        onClick={prev}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/60 backdrop-blur text-foreground-950 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={next}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/60 backdrop-blur text-foreground-950 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-primary-500" : "w-2.5 bg-foreground-500/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}